/**
 * Workflow state badge - visually consistent with the registration
 * ``StatusBadge`` so the two columns read as siblings rather than
 * different design eras.
 *
 * Label resolution is key-first, label-second so tenants can
 * translate or override server text without redeploying:
 *
 *   1. ``workflows.{workflowKey}.states.{stateKey}``
 *   2. ``workflows.states.{stateKey}``
 *   3. server-provided ``label`` (English fallback from the seed)
 *   4. ``stateKey`` itself
 *
 * The visual treatment mirrors ``StatusBadge``: rounded-[4px], 10px
 * uppercase, tinted background derived from the workflow state's
 * advisory colour (so a green "Paid" reads the same as a green
 * "Active").
 */

import { Badge } from '@/shared/components/ui/badge'
import { cn } from '@/shared/lib/utils'

import { useWorkflowStateLabel } from './use-workflow-i18n'

export interface WorkflowStateBadgeProps {
  /** Stable machine identifier - drives the i18n lookup. */
  stateKey?: string
  /** Optional workflow key for tenant-specific translation overrides. */
  workflowKey?: string
  /** English fallback label from the workflow seed. */
  label: string
  /** Advisory colour from the workflow state definition. */
  color?: string | null
  isTerminal?: boolean
  className?: string
}

export function WorkflowStateBadge({
  stateKey,
  workflowKey,
  label,
  color,
  isTerminal,
  className,
}: WorkflowStateBadgeProps) {
  const resolvedLabel = useWorkflowStateLabel(stateKey, label, workflowKey)

  // Tinted background mirroring StatusBadge's neutral-with-API-color
  // treatment: ~8% alpha background, full-strength foreground. We
  // intentionally do NOT borrow StatusBadge's variant detector here -
  // workflow colours are authoritative because they're authored
  // alongside the state, not heuristically derived from a label.
  const style = color
    ? { backgroundColor: `${color}14`, color }
    : undefined

  return (
    <Badge
      className={cn(
        'h-auto rounded-[4px] border-transparent px-2.5 py-1 text-[10px] font-normal uppercase tracking-[0.08em]',
        isTerminal && 'opacity-80',
        !color && 'bg-[var(--bg-subtle)] text-[var(--ink-3)]',
        className,
      )}
      style={style}
    >
      {resolvedLabel}
    </Badge>
  )
}
